import { LDSHeading, LDSMessages } from "@resmed/lumina-design-system";
import { getTimeOnly } from "../../utils/DateHelper";
import { DELIVERED_ICON, READ_ICON, SVGIconConfig, TICK_ICON } from "../../utils/SVGHelper";
import type { DeliveryStatus, CalloutData } from "../../features/chat/types/UIMessage";
import RepliedWidget from "../RepliedWidget";
import MessageContent from "../MessageContent";

export type { DeliveryStatus };

export interface SenderChatBubbleProps {
  id: string;
  content: string;
  timestamp: string;
  status: DeliveryStatus;
  calloutData?: CalloutData[];
  isDeleted?: boolean;
  hasAttachment?: boolean;
}

const STATUS_LABEL: Record<DeliveryStatus, string | null> = {
  sent: null,
  delivered: "Delivered",
  read: "Read",
};

const STATUS_ICON: Record<DeliveryStatus, SVGIconConfig> = {
  sent: TICK_ICON,
  delivered: DELIVERED_ICON,
  read: READ_ICON,
};

const StatusIcon = ({ status }: { status: DeliveryStatus }) => {
  const { src, alt, width, height } = STATUS_ICON[status];
  return <img src={src} alt={alt} width={width} height={height} />;
};

const SenderChatBubble = ({
  id,
  content,
  timestamp,
  status,
  calloutData,
  isDeleted,
  hasAttachment,
}: SenderChatBubbleProps) => {
  const label = STATUS_LABEL[status];

  return (
    <div className="lds-flex lds-flex-col lds-items-end">
      {/* Timestamp above bubble */}
      <LDSHeading
        size="h5"
        className="lds-typography-xs-medium lds-text-textcolor-weak lds-mb-1"
      >
        {getTimeOnly(timestamp)}
      </LDSHeading>
      {/* key includes state flags so PrimeReact Messages remounts when deletion/attachment changes */}
      <div className="lds-max-w-[75%] lds-w-full">
        <LDSMessages
          key={`${id}-${String(isDeleted)}-${String(hasAttachment)}`}
          id={id}
          severity="info"
          viewIcon={false}
          detail={
            calloutData && calloutData.length > 0 ? (
              <>
                <RepliedWidget calloutData={calloutData} />
                <MessageContent content={content} isDeleted={isDeleted} hasAttachment={hasAttachment} />
              </>
            ) : (
              <MessageContent content={content} isDeleted={isDeleted} hasAttachment={hasAttachment} />
            )
          }
          closable={false}
          sticky={true}
          subtle={true}
          wrapperClassName="!lds-px-2"
        />
      </div>
      {/* Delivery status below */}
      <div className="lds-flex lds-items-center lds-gap-1 lds-mt-1">
        <StatusIcon status={status} />
        {label && (
          <span className="lds-text-xs lds-font-normal lds-leading-[125%] lds-text-textcolor-caption">
            {label}
          </span>
        )}
      </div>
    </div>
  );
};

export default SenderChatBubble;
